// src/components/Experiment/JobSelectionStage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Row, Col, Card, Button, Alert, Spinner, Modal, Form, InputGroup } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { getJobsIds, getJobResults } from '../../api/photoProcessingApi';
import JobSelectionCard from './JobSelectionCard';
import './JobSelectionStage.css';

const JobSelectionStage = ({
  name,
  description,
  date,
  selectedJobs,
  onNameChange,
  onDescriptionChange,
  onDateChange,
  onJobsChange,
  nameValidation
}) => {
  const [availableJobs, setAvailableJobs] = useState([]);
  const [jobIds, setJobIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const loadingRef = useRef(false);
  const isMountedRef = useRef(true);
  const observerRef = useRef();
  const lastJobRef = useCallback(node => {
    if (loadingMore || initialLoading) return;
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMorePages && !loadingMore && !initialLoading) {
        loadMoreJobs();
      }
    });

    if (node) observerRef.current.observe(node);
  }, [loadingMore, initialLoading, hasMorePages]);

  useEffect(() => {
    isMountedRef.current = true;
    loadAvailableJobs(1, true);

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Generate default experiment name
  const generateDefaultExperimentName = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `Experiment_${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
  };

  // Set default name when component mounts and name is empty
  useEffect(() => {
    if (isInitialLoad && (!name || name.trim() === '')) {
      const defaultName = generateDefaultExperimentName();
      onNameChange(defaultName);
      setIsInitialLoad(false);
    }
  }, [name, isInitialLoad, onNameChange]);

  // Also set default date if not provided
  useEffect(() => {
    if (!date) {
      const now = new Date();
      onDateChange(now.toISOString());
    }
  }, [date, onDateChange]);

  const loadAvailableJobs = async (pageNum = 1, reset = false) => {
    // Prevent concurrent loads
    if (loadingRef.current) return;

    try {
      loadingRef.current = true;
      if (reset) {
        setInitialLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const [responseJobIds, more] = await getJobsIds(pageNum);

      if (!isMountedRef.current) return;

      setHasMorePages(more);
      setCurrentPage(pageNum);

      // Filter out already loaded job IDs
      const newIds = responseJobIds.filter(id => !jobIds.has(id));

      if (newIds.length === 0) {
        if (reset && responseJobIds.length === 0) {
          setAvailableJobs([]);
        }
        return;
      }

      // Update IDs set
      setJobIds(prev => {
        const newSet = new Set(prev);
        newIds.forEach(id => newSet.add(id));
        return newSet;
      });

      // Load job details for new IDs only
      const results = await Promise.allSettled(
        newIds.map(async jobId => {
          try {
            const jobData = await getJobResults(jobId);
            return { jobId, data: jobData, error: null };
          } catch (error) {
            console.error(`Error loading job ${jobId}:`, error);
            return { jobId, data: null, error };
          }
        })
      );

      if (!isMountedRef.current) return;

      const successfulJobs = results
        .filter(result => result.status === 'fulfilled' && result.value?.error === null)
        .map(result => result.value.data)
        .filter(job => job !== null && job.status === 'completed')
        .map(job => ({
          id: job.id,
          original_image_filename: job.original_image_filename,
          rotated_image: job.rotated_image,
          thumbnail: job.thumbnail,
          extracted_colors: job.extracted_colors,
          status: job.status,
          crop_type_display: job.crop_type_display,
          orientation_display: job.orientation_display,
          image_type_display: job.image_type_display,
          grid_dimensions: job.adjusted_grid_dimensions || job.grid_dimensions,
          created_at: job.created_at,
          calculate_type: 'rgb'
        }));

      // Merge new jobs with existing ones (no duplicates)
      if (reset) {
        setAvailableJobs(successfulJobs);
      } else {
        setAvailableJobs(prev => {
          const existingIds = new Set(prev.map(j => j.id));
          const newJobs = successfulJobs.filter(job => !existingIds.has(job.id));
          return [...prev, ...newJobs];
        });
      }

    } catch (error) {
      console.error('Error loading jobs:', error);
      if (isMountedRef.current) {
        setError('Failed to load available jobs');
      }
    } finally {
      if (isMountedRef.current) {
        if (reset) {
          setInitialLoading(false);
        } else {
          setLoadingMore(false);
        }
        loadingRef.current = false;
      }
    }
  };

  const loadMoreJobs = async () => {
    if (hasMorePages && !loadingMore && !initialLoading && !loadingRef.current) {
      const nextPage = currentPage + 1;
      await loadAvailableJobs(nextPage, false);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;

    if (source.droppableId === 'available' && destination.droppableId === 'selected') {
      // Moving from available to selected
      const jobToMove = availableJobs[source.index];
      const newSelectedJobs = Array.from(selectedJobs);
      newSelectedJobs.splice(destination.index, 0, jobToMove);

      onJobsChange(newSelectedJobs);
      setAvailableJobs(prev => prev.filter(job => job.id !== jobToMove.id));

    } else if (source.droppableId === 'selected' && destination.droppableId === 'available') {
      // Moving from selected to available
      const jobToMove = selectedJobs[source.index];
      const newSelectedJobs = Array.from(selectedJobs);
      newSelectedJobs.splice(source.index, 1);

      onJobsChange(newSelectedJobs);
      setAvailableJobs(prev => [...prev, jobToMove]);

    } else if (source.droppableId === 'selected' && destination.droppableId === 'selected') {
      // Reordering within selected
      const newSelectedJobs = Array.from(selectedJobs);
      const [removed] = newSelectedJobs.splice(source.index, 1);
      newSelectedJobs.splice(destination.index, 0, removed);

      onJobsChange(newSelectedJobs);
    }
  };

  const addJobToSelected = (job) => {
    onJobsChange([...selectedJobs, job]);
    setAvailableJobs(prev => prev.filter(j => j.id !== job.id));
  };

  const removeJobFromSelected = (jobId) => {
    const jobToRemove = selectedJobs.find(job => job.id === jobId);
    if (jobToRemove) {
      onJobsChange(selectedJobs.filter(job => job.id !== jobId));
      setAvailableJobs(prev => [...prev, jobToRemove]);
    }
  };

  const filteredJobs = availableJobs.filter(job =>
    job.id.toString().includes(searchTerm) ||
    (job.original_image_filename && job.original_image_filename.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
    return localDate.toISOString().slice(0, 16);
  };

  const parseDateFromInput = (inputValue) => {
    if (!inputValue) return null;
    const date = new Date(inputValue);
    return date.toISOString();
  };

  const handleRegenerateDefaultName = () => {
    const newDefaultName = generateDefaultExperimentName();
    onNameChange(newDefaultName);
  };

  if (initialLoading) {
    return (
      <div className="job-selection-stage">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading available jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="job-selection-stage">
      {/* Experiment Details Section */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Experiment Details</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Experiment Name *</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Enter experiment name"
                    value={name}
                    onChange={(e) => onNameChange(e.target.value)}
                    isInvalid={!nameValidation.isValid && nameValidation.isTouched}
                    className="experiment-name-input"
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={handleRegenerateDefaultName}
                    title="Generate new default name with current timestamp"
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                  </Button>
                  <Form.Control.Feedback type="invalid">
                    {nameValidation.message}
                  </Form.Control.Feedback>
                </InputGroup>
                {nameValidation.isValid && (
                  <Form.Text className="text-success">
                    <i className="bi bi-check-circle me-1"></i>
                    Valid name
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Experiment Date & Time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={formatDateForInput(date)}
                  onChange={(e) => onDateChange(parseDateFromInput(e.target.value))}
                  className="experiment-date-input"
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Describe the experiment purpose, conditions, or any relevant details..."
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              className="experiment-description-input"
            />
          </Form.Group>
        </Card.Body>
      </Card>

      <div className="mb-4">
        <h4>Select Analysis Images</h4>
        <p className="text-muted">
          Choose the images that represent different time points of your chemical reaction.
          Drag and drop to arrange them in chronological order.
        </p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Row>
          {/* Available Jobs */}
          <Col md={6}>
            <Card className="h-100">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Available Images ({availableJobs.length})</h6>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowJobModal(true)}
                >
                  <i className="bi bi-search me-1"></i>
                  Browse
                </Button>
              </Card.Header>
              <Card.Body className="d-flex flex-column">
                <Droppable droppableId="available">
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`job-drop-zone ${snapshot.isDraggingOver ? 'drag-over' : ''} flex-grow-1`}
                      style={{ minHeight: '400px', maxHeight: '600px', overflowY: 'auto' }}
                    >
                      {filteredJobs.map((job, index) => (
                        <Draggable key={job.id} draggableId={`job-${job.id}`} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`mb-2 ${snapshot.isDragging ? 'dragging' : ''}`}
                            >
                              <div ref={index === filteredJobs.length - 1 ? lastJobRef : null}>
                                <JobSelectionCard
                                  job={job}
                                  onAdd={() => addJobToSelected(job)}
                                  showAddButton={true}
                                />
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}

                      {filteredJobs.length === 0 && !loadingMore && (
                        <div className="text-center text-muted py-4">
                          No available jobs found
                        </div>
                      )}

                      {/* Loading indicator for more items */}
                      {loadingMore && (
                        <div className="text-center py-3">
                          <Spinner animation="border" size="sm" />
                          <p className="text-muted small mt-2">Loading more jobs...</p>
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </Card.Body>
            </Card>
          </Col>

          {/* Selected Jobs */}
          <Col md={6}>
            <Card className="h-100">
              <Card.Header>
                <h6 className="mb-0">
                  Selected Images ({selectedJobs.length})
                  {selectedJobs.length > 0 && (
                    <small className="text-muted ms-2">
                      Drag to reorder chronologically
                    </small>
                  )}
                </h6>
              </Card.Header>
              <Card.Body>
                <Droppable droppableId="selected">
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`job-drop-zone ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
                      style={{ minHeight: '400px', maxHeight: '600px', overflowY: 'auto' }}
                    >
                      {selectedJobs.map((job, index) => (
                        <Draggable key={job.id} draggableId={`job-${job.id}`} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`mb-2 ${snapshot.isDragging ? 'dragging' : ''}`}
                            >
                              <JobSelectionCard
                                job={job}
                                onRemove={() => removeJobFromSelected(job.id)}
                                showRemoveButton={true}
                                chronologicalIndex={index + 1}
                                calculateType={job.calculateType}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}

                      {selectedJobs.length === 0 && (
                        <div className="text-center text-muted py-4">
                          <i className="bi bi-arrow-left me-2"></i>
                          Drag images here to select them
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </DragDropContext>

      {/* Job Browser Modal */}
      <Modal show={showJobModal} onHide={() => setShowJobModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Browse Available Jobs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            type="text"
            placeholder="Search by job ID or filename..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-3"
          />

          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {filteredJobs.map((job, index) => (
              <div key={job.id} ref={index === filteredJobs.length - 1 ? lastJobRef : null}>
                <JobSelectionCard
                  job={job}
                  onAdd={() => {
                    addJobToSelected(job);
                    setShowJobModal(false);
                  }}
                  showAddButton={true}
                  compact={true}
                />
              </div>
            ))}

            {loadingMore && (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
                <p className="text-muted small mt-2">Loading more jobs...</p>
              </div>
            )}

            {!hasMorePages && filteredJobs.length > 0 && (
              <div className="text-center py-3">
                <p className="text-muted small">No more jobs to load</p>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowJobModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default JobSelectionStage;